import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import type { AeTemplateDetail } from '@/types/assessmentEngine';
import { assessmentEngineService } from '@/services/assessmentEngineService';
import { refreshAndPersistDimensions } from './dimensionSync';
import {
  type BuilderDragData,
  isDemographicsSectionId,
  parseDndId,
  sectionDndId,
} from './builderDnd';
import { findSectionById, topLevelContentSections } from './structureMoveUtils';
import { DEMOGRAPHICS_SECTION_CODE } from './templateConfigTypes';

function insertIndexAfterMove(containerIds: string[], activeId: string, overId: string): number {
  const oldIndex = containerIds.indexOf(activeId);
  if (oldIndex === -1) {
    const targetIndex = containerIds.indexOf(overId);
    return targetIndex === -1 ? containerIds.length : targetIndex;
  }
  const newIndex = containerIds.indexOf(overId);
  if (newIndex === -1) return oldIndex;
  return arrayMove(containerIds, oldIndex, newIndex).indexOf(activeId);
}

function sectionQuestionDndIds(template: AeTemplateDetail, sectionId: string): string[] {
  const section = findSectionById(template, sectionId);
  return (section?.questions ?? []).map((q) => `question:${q.id}`);
}

function parentSubsectionDndIds(template: AeTemplateDetail, parentSectionId: string): string[] {
  const parent = findSectionById(template, parentSectionId);
  return (parent?.subsections ?? []).map((s) => sectionDndId(s.id));
}

function findQuestionSection(
  template: AeTemplateDetail,
  questionId: string
): { sectionId: string; index: number } | null {
  for (const sec of template.sections) {
    if (sec.code === DEMOGRAPHICS_SECTION_CODE) continue;
    const qIdx = sec.questions.findIndex((q) => q.id === questionId);
    if (qIdx >= 0) return { sectionId: sec.id, index: qIdx };
    for (const sub of sec.subsections) {
      const subIdx = sub.questions.findIndex((q) => q.id === questionId);
      if (subIdx >= 0) return { sectionId: sub.id, index: subIdx };
    }
  }
  return null;
}

function resolveQuestionDrop(
  template: AeTemplateDetail,
  active: BuilderDragData,
  overId: string
): { sectionId: string; insertIndex: number } | null {
  if (active.kind !== 'question') return null;
  const activeDndId = `question:${active.questionId}`;

  if (overId.startsWith('questions-drop:')) {
    const sectionId = overId.slice('questions-drop:'.length);
    if (isDemographicsSectionId(template, sectionId)) return null;
    const ids = sectionQuestionDndIds(template, sectionId).filter((id) => id !== activeDndId);
    return { sectionId, insertIndex: ids.length };
  }

  const overParsed = parseDndId(overId);
  if (!overParsed) return null;

  if (overParsed.kind === 'question') {
    const overLoc = findQuestionSection(template, overParsed.entityId);
    if (!overLoc || isDemographicsSectionId(template, overLoc.sectionId)) return null;
    const ids = sectionQuestionDndIds(template, overLoc.sectionId);
    return {
      sectionId: overLoc.sectionId,
      insertIndex: insertIndexAfterMove(ids, activeDndId, overId),
    };
  }

  if (overParsed.kind === 'section') {
    const target = findSectionById(template, overParsed.entityId);
    if (!target || isDemographicsSectionId(template, target.id)) return null;
    const ids = sectionQuestionDndIds(template, target.id).filter((id) => id !== activeDndId);
    return { sectionId: target.id, insertIndex: ids.length };
  }

  return null;
}

function resolveSectionDrop(
  template: AeTemplateDetail,
  active: BuilderDragData,
  overId: string
): { parentSectionId: string | null; insertIndex: number } | null {
  if (active.kind !== 'section') return null;
  const activeDndId = sectionDndId(active.sectionId);

  if (overId.startsWith('subsection-drop:')) {
    const parentId = overId.slice('subsection-drop:'.length);
    if (isDemographicsSectionId(template, parentId)) return null;
    const ids = parentSubsectionDndIds(template, parentId).filter((id) => id !== activeDndId);
    return { parentSectionId: parentId, insertIndex: ids.length };
  }

  const overParsed = parseDndId(overId);
  if (!overParsed || overParsed.kind !== 'section') return null;

  const activeSection = findSectionById(template, active.sectionId);
  const overSection = findSectionById(template, overParsed.entityId);
  if (!activeSection || !overSection || isDemographicsSectionId(template, active.sectionId)) {
    return null;
  }

  const activeIsSubsection = activeSection.parent_section_id != null;
  const overIsSubsection = overSection.parent_section_id != null;

  if (!activeIsSubsection && !overIsSubsection) {
    const ids = topLevelContentSections(template).map((s) => sectionDndId(s.id));
    return {
      parentSectionId: null,
      insertIndex: insertIndexAfterMove(ids, activeDndId, overId),
    };
  }

  if (activeIsSubsection && overIsSubsection) {
    if (activeSection.parent_section_id === overSection.parent_section_id) {
      const parentId = activeSection.parent_section_id!;
      const ids = parentSubsectionDndIds(template, parentId);
      return {
        parentSectionId: parentId,
        insertIndex: insertIndexAfterMove(ids, activeDndId, overId),
      };
    }
    const newParentId = overSection.parent_section_id!;
    const ids = parentSubsectionDndIds(template, newParentId).filter((id) => id !== activeDndId);
    const overIndex = ids.indexOf(overId);
    return {
      parentSectionId: newParentId,
      insertIndex: overIndex === -1 ? ids.length : overIndex,
    };
  }

  if (activeIsSubsection && !overIsSubsection) {
    const ids = parentSubsectionDndIds(template, overSection.id).filter((id) => id !== activeDndId);
    return { parentSectionId: overSection.id, insertIndex: ids.length };
  }

  return null;
}

export async function handleTemplateBuilderDragEnd(
  event: DragEndEvent,
  template: AeTemplateDetail,
  templateId: string,
  onReload: () => void
): Promise<void> {
  const { active, over } = event;
  if (!over || active.id === over.id) return;

  const activeData = active.data.current as BuilderDragData | undefined;
  if (!activeData) return;

  const overId = String(over.id);

  if (activeData.kind === 'question') {
    const drop = resolveQuestionDrop(template, activeData, overId);
    if (!drop) return;
    if (isDemographicsSectionId(template, drop.sectionId)) return;

    await assessmentEngineService.moveQuestion(templateId, activeData.questionId, {
      section_id: drop.sectionId,
      insert_index: drop.insertIndex,
    });
    await refreshAndPersistDimensions(templateId);
    onReload();
    return;
  }

  if (activeData.kind === 'section') {
    const drop = resolveSectionDrop(template, activeData, overId);
    if (!drop) return;

    await assessmentEngineService.moveSection(templateId, activeData.sectionId, {
      parent_section_id: drop.parentSectionId,
      insert_index: drop.insertIndex,
    });
    await refreshAndPersistDimensions(templateId);
    onReload();
  }
}
